import React, { useState, useEffect, useCallback, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Layers,
  Loader2,
  Check,
  X,
  Image as ImageIcon,
  FileText,
  TrendingUp,
  TrendingDown,
  Calendar,
  Hash,
  Store,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Link2,
  Keyboard,
  Sparkles,
} from "lucide-react";

// react-pdf needs a worker; resolve it from the bundled pdfjs-dist.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const MIN_SCORE = 60;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  const abs = Math.abs(amount).toFixed(2);
  return `${currency || ""}${abs}`.trim();
};

const scoreColor = (score) => {
  if (score >= 85) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (score >= 70) return "bg-teal-100 text-teal-700 border-teal-200";
  return "bg-amber-100 text-amber-700 border-amber-200";
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Base64 payload -> object URL. A blob URL is far more reliable than a giant
// data: URI for react-pdf (no ArrayBuffer detach on re-render) and lets the
// native browser viewer render the same bytes as a fallback.
const base64ToBlobUrl = (b64, contentType) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
};

// The document values worth highlighting on the receipt so a reviewer can
// eyeball the match: reference number, amount (in a few locale spellings) and
// merchant name. These map directly to the "reference / amount / merchant"
// match reasons on the right.
const buildHighlightTerms = (doc) => {
  const terms = new Set();
  if (doc.reference) {
    const ref = String(doc.reference).trim();
    if (ref.length >= 3) terms.add(ref);
  }
  if (doc.merchant_name) {
    const m = String(doc.merchant_name).trim();
    if (m.length >= 3) terms.add(m);
  }
  if (doc.amount !== undefined && doc.amount !== null) {
    const n = Math.abs(Number(doc.amount));
    if (!isNaN(n) && n > 0) {
      const twoDot = n.toFixed(2); // 7260.00
      const [intStr, dec] = twoDot.split(".");
      const grouped = (sep) => intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      terms.add(twoDot); // 7260.00
      terms.add(`${intStr},${dec}`); // 7260,00
      terms.add(`${grouped(".")},${dec}`); // 7.260,00 (nl)
      terms.add(`${grouped(",")}.${dec}`); // 7,260.00 (en)
      if (intStr.length >= 3) terms.add(intStr); // 7260
    }
  }
  // Longest first so the regex prefers full amounts over their integer part.
  return [...terms].filter(Boolean).sort((a, b) => b.length - a.length);
};

// Track a container's width so the PDF page can scale to fit.
const useContainerWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const update = () => setWidth(ref.current?.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

const PdfDocumentViewer = ({ fileUrl, terms }) => {
  const [containerRef, containerWidth] = useContainerWidth();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [naturalWidth, setNaturalWidth] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPageNumber(1);
    setError(false);
  }, [fileUrl]);

  const textRenderer = useCallback(
    ({ str }) => {
      if (!str || terms.length === 0) return str;
      const pattern = new RegExp(`(${terms.map(escapeRegex).join("|")})`, "gi");
      return str.replace(pattern, '<mark class="pdf-hl">$1</mark>');
    },
    [terms]
  );

  // Scale to fit the pane, but never upscale past the page's own width.
  const pad = 32;
  const target = Math.max(containerWidth - pad, 240);
  const renderWidth = naturalWidth ? Math.min(target, naturalWidth) : target;

  // pdf.js couldn't render (worker/version quirk, unusual PDF structure). The
  // browser's native viewer is far more tolerant, so fall back to it. We lose
  // the field highlighting there, but the reviewer still sees the document.
  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 px-4 py-1.5 text-[11px] text-amber-600 bg-amber-50 border-b border-amber-100 text-center">
          Highlighting unavailable for this file — showing native preview
        </div>
        <iframe
          src={fileUrl}
          title="PDF preview"
          className="flex-1 w-full border-0 bg-white"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      <div className="flex-1 overflow-auto flex justify-center p-4">
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={(err) => {
            console.warn("react-pdf render failed, using native fallback:", err);
            setError(true);
          }}
          loading={
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={renderWidth > 0 ? renderWidth : undefined}
            renderAnnotationLayer={false}
            customTextRenderer={textRenderer}
            onLoadSuccess={(page) => setNaturalWidth(page.originalWidth)}
            className="shadow-lg rounded overflow-hidden bg-white"
          />
        </Document>
      </div>
      {numPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3 py-2 border-t bg-white/70">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-slate-500">
            Page {pageNumber} / {numPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const ImageDocumentViewer = ({ fileUrl, filename }) => {
  const [natW, setNatW] = useState(null);
  return (
    <div className="h-full flex items-center justify-center p-4 overflow-auto">
      <img
        src={fileUrl}
        alt={filename}
        onLoad={(e) => setNatW(e.target.naturalWidth)}
        className="object-contain"
        style={{
          maxWidth: natW ? `min(100%, ${natW}px)` : "100%",
          maxHeight: "100%",
        }}
      />
    </div>
  );
};

// Loads the file payload once, then renders a PDF (with highlights) or an image.
const DocumentPane = ({ fileId, filename, contentType, terms }) => {
  const [loading, setLoading] = useState(true);
  const [fileUrl, setFileUrl] = useState(null);
  const [ct, setCt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    setLoading(true);
    setFileUrl(null);
    setCt(null);
    apiService
      .getFile(fileId)
      .then((res) => {
        if (cancelled) return;
        const type = res.data?.content_type || contentType || "";
        setCt(type);
        if (res.data?.data) {
          url = base64ToBlobUrl(res.data.data, type);
          setFileUrl(url);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId, contentType]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
      </div>
    );
  }

  if (ct === "application/pdf" && fileUrl) {
    return <PdfDocumentViewer fileUrl={fileUrl} terms={terms} />;
  }
  if (ct?.startsWith("image/") && fileUrl) {
    return <ImageDocumentViewer fileUrl={fileUrl} filename={filename} />;
  }
  return (
    <div className="h-full flex flex-col items-center justify-center text-slate-400">
      {ct?.startsWith("image/") ? (
        <ImageIcon className="w-12 h-12 mb-2" />
      ) : (
        <FileText className="w-12 h-12 mb-2" />
      )}
      <p className="text-sm">Preview not available</p>
    </div>
  );
};

const TransactionSummary = ({ tx, highlight }) => {
  // Amounts are stored as positive magnitudes; direction carries the sign.
  // Using amount >= 0 rendered every transaction as income (green "+").
  const isIncome = tx?.direction === "income";
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isIncome ? "bg-emerald-100" : "bg-red-100"
          }`}
        >
          {isIncome ? (
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {tx?.counterparty_name || "Transaction"}
          </p>
          <p className="text-xs text-slate-500">{formatDate(tx?.transaction_date)}</p>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p
          className={`text-sm font-semibold ${
            isIncome ? "text-emerald-600" : "text-red-500"
          }`}
        >
          {isIncome ? "+" : "-"}
          {formatAmount(tx?.amount, tx?.currency)}
        </p>
        {highlight && (
          <Badge
            variant="outline"
            className={`mt-0.5 h-5 px-1.5 text-[10px] ${scoreColor(tx?.score)}`}
          >
            {tx?.score}% match
          </Badge>
        )}
      </div>
    </div>
  );
};

const SwipeMatchPage = ({ onClose }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [linkedCount, setLinkedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [acting, setActing] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [chosenTx, setChosenTx] = useState(null); // override the top match

  const total = cards.length;
  const current = index < cards.length ? cards[index] : null;
  const activeMatch = chosenTx || current?.top_match;
  const doc = current?.document_info || {};
  const highlightTerms = current ? buildHighlightTerms(doc) : [];

  const fetchCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getMatchQueue({ min_score: MIN_SCORE });
      setCards(res.data.cards || []);
      setIndex(0);
      setProcessed(0);
      setLinkedCount(0);
      setSkippedCount(0);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load the match queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    setShowAlternatives(false);
    setChosenTx(null);
  }, [index]);

  const advance = useCallback(() => {
    setProcessed((p) => p + 1);
    setIndex((i) => i + 1);
  }, []);

  const handleSkip = useCallback(() => {
    if (acting || !current) return;
    setSkippedCount((c) => c + 1);
    advance();
  }, [acting, current, advance]);

  const handleConfirm = useCallback(async () => {
    if (acting || !current || !activeMatch) return;
    setActing(true);
    try {
      await apiService.attachFile(activeMatch.transaction_id, current.file_id);
      toast.success(`Linked to ${activeMatch.counterparty_name || "transaction"}`);
      setLinkedCount((c) => c + 1);
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link");
    } finally {
      setActing(false);
    }
  }, [acting, current, activeMatch, advance]);

  // Keyboard: arrows swipe, escape closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose?.();
        return;
      }
      if (!current || acting) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, acting, handleConfirm, handleSkip, onClose]);

  const done = !loading && !error && !current;

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      {/* Dimmed, blurred backdrop so the inbox stays visible behind. */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative m-auto w-[calc(100vw-1.5rem)] h-[calc(100vh-1.5rem)] md:w-[calc(100vw-3rem)] md:h-[calc(100vh-3rem)] max-w-[1500px] bg-white rounded-2xl shadow-2xl flex overflow-hidden animate-fade-in">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-3" />
            <p className="text-slate-500">Finding confident matches…</p>
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-slate-700 font-medium">{error}</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button variant="outline" onClick={fetchCards}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Try again
              </Button>
            </div>
          </div>
        ) : done ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-20 h-20 rounded-3xl bg-emerald-50 flex items-center justify-center mb-5">
              <Check className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              {total === 0 ? "Nothing to review" : "All caught up"}
            </h2>
            <p className="text-slate-500 mt-1 max-w-sm">
              {total === 0
                ? "No unlinked receipts have a confident transaction match right now."
                : `You linked ${linkedCount} and skipped ${skippedCount} of ${total} receipts.`}
            </p>
            <div className="flex items-center gap-2 mt-6">
              <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700">
                Back to inbox
              </Button>
              <Button variant="outline" onClick={fetchCards}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Refresh queue
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Left: document, as large as the pane allows */}
            <div className="hidden md:flex flex-col flex-1 min-w-0 bg-slate-100 border-r">
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b bg-white/70">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <FileText className="w-3.5 h-3.5" />
                  Receipt
                </div>
                <span className="text-xs text-slate-400 truncate max-w-[60%]">
                  {current.filename}
                </span>
              </div>
              <div className="flex-1 min-h-0">
                <DocumentPane
                  fileId={current.file_id}
                  filename={current.filename}
                  contentType={current.content_type}
                  terms={highlightTerms}
                />
              </div>
            </div>

            {/* Right: details + actions */}
            <div className="flex flex-col w-full md:w-[420px] flex-shrink-0">
              {/* Header */}
              <div className="flex-shrink-0 px-4 pt-3 pb-3 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Layers className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h1 className="text-sm font-semibold text-slate-900">
                        Match Wizard
                      </h1>
                      <p className="text-[11px] text-slate-500">
                        {processed} / {total} · {linkedCount} linked
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700"
                    onClick={onClose}
                    title="Close (Esc)"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <Progress value={(processed / total) * 100} className="h-1.5 mt-3" />
              </div>

              {/* Scrollable details */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Receipt fields (mobile also gets a compact preview link) */}
                <div className="rounded-lg border bg-slate-50/60 p-3 space-y-1.5">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                    Receipt details
                  </p>
                  {doc.merchant_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Store className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-medium text-slate-900 truncate">
                        {doc.merchant_name}
                      </span>
                    </div>
                  )}
                  {doc.amount !== undefined && doc.amount !== null && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-3.5 text-center text-slate-400 font-semibold">
                        {doc.currency || "€"}
                      </span>
                      <span className="font-bold text-emerald-600">
                        {formatAmount(doc.amount, doc.currency)}
                      </span>
                    </div>
                  )}
                  {doc.date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-700">{formatDate(doc.date)}</span>
                    </div>
                  )}
                  {doc.reference && (
                    <div className="flex items-center gap-2 text-sm">
                      <Hash className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-mono text-xs text-slate-600 truncate">
                        {doc.reference}
                      </span>
                    </div>
                  )}
                </div>

                {/* Suggested match */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <Sparkles className="w-3.5 h-3.5" />
                      Suggested match
                    </div>
                    <Badge
                      variant="outline"
                      className={`h-6 px-2 text-xs font-semibold ${scoreColor(
                        activeMatch?.score || 0
                      )}`}
                    >
                      {activeMatch?.score}%
                    </Badge>
                  </div>
                  <div className="rounded-lg border p-3 bg-white">
                    <TransactionSummary tx={activeMatch} />
                  </div>

                  {activeMatch?.reasons?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {activeMatch.reasons.map((reason, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="h-5 px-1.5 text-[10px] bg-slate-50 text-slate-600 border-slate-200"
                        >
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Alternatives */}
                {current.alternatives?.length > 0 && (
                  <div className="pt-1">
                    <button
                      onClick={() => setShowAlternatives((v) => !v)}
                      className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      {showAlternatives ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                      {current.alternatives.length} other candidate
                      {current.alternatives.length > 1 ? "s" : ""}
                    </button>
                    {showAlternatives && (
                      <div className="mt-2 space-y-2">
                        {[current.top_match, ...current.alternatives].map((alt) => {
                          const selected =
                            activeMatch?.transaction_id === alt.transaction_id;
                          return (
                            <button
                              key={alt.transaction_id}
                              onClick={() =>
                                setChosenTx(
                                  alt.transaction_id ===
                                    current.top_match.transaction_id
                                    ? null
                                    : alt
                                )
                              }
                              className={`w-full text-left rounded-lg border p-2 transition-colors ${
                                selected
                                  ? "border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/40"
                                  : "hover:border-slate-300"
                              }`}
                            >
                              <TransactionSummary tx={alt} highlight />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex-shrink-0 border-t p-4">
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 w-14 rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 p-0"
                    onClick={handleSkip}
                    disabled={acting}
                    title="Skip (←)"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                  <Button
                    size="lg"
                    className="h-16 w-16 rounded-full bg-emerald-600 hover:bg-emerald-700 p-0 shadow-lg shadow-emerald-200"
                    onClick={handleConfirm}
                    disabled={acting}
                    title="Link (→)"
                  >
                    {acting ? (
                      <Loader2 className="w-7 h-7 animate-spin" />
                    ) : (
                      <Link2 className="w-7 h-7" />
                    )}
                  </Button>
                </div>
                <p className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-slate-400">
                  <Keyboard className="w-3.5 h-3.5" />
                  ← skip · → link · Esc close
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SwipeMatchPage;
